import { useQuery } from '@tanstack/react-query';
import { CircleArrowUp, Gift } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { QueryKey } from '@/Config/api/queryKey';
import { IndexPath, SettingsTabsEnum } from '@/Config/app/router/index.path';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { useSubscription } from '@/Domain/components/SubscriptionProvider';
import type { AIAssistantsData } from '@/Domain/components/TopBar/types';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { TierEnum } from '@/Domain/enum/Tier.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useGetSubscription } from '@/Domain/hooks/Subscription/useGetSubscription.hook';
import { cn } from '@/Domain/utils/cn';
import {
  formatDiscountRemaining,
  useOnboardingDiscount,
} from '@/UI/GettingStarted/components/onboardingDiscount';

type Size = 'xs' | 'sm' | 'md';

export type UpgradeCtaSource =
  | 'kb_add_content_banner'
  | 'kb_auto_refresh_banner'
  | 'kb_full_pill'
  | 'kb_limit_modal'
  | 'top_bar'
  | 'app_sidebar'
  | 'unspecified';

interface Props {
  iconOnly?: boolean;
  size?: Size;
  className?: string;
  source?: UpgradeCtaSource;
}

export const UpgradePlanButton = ({
  iconOnly = false,
  size = 'md',
  className,
  source = 'unspecified',
}: Props) => {
  const { t } = useTranslation('sidebar_menu');
  const navigate = useNavigate();
  const { scenario } = useParams<{ scenario: string }>();
  const { data } = useGetSubscription();
  const { getConfig } = useSubscription();
  const { capture } = usePostHogAnalytics();
  const tier = data?.current_plan.tier;

  // Hide the button entirely when the user can't open the Billing tab — same
  // gate the SettingsPage uses (`!permissions.subscription` ⇒ tab disabled).
  // Without this, an editor/support user would see an Upgrade CTA that
  // navigates to a tab they can't access.
  const { data: aiAssistants } = useQuery<AIAssistantsData>({
    queryKey: [QueryKey.aiAssistants.get],
  });
  const hasBillingAccess = !!aiAssistants?.current_ai_assistant?.permissions?.subscription;

  // Brief click feedback. Navigation to /settings is near-instant, but
  // without any visual response the click feels unregistered. Show a
  // spinner for ~500ms after the click so the user sees the action took.
  // Declared above the early-return below so hook order stays stable
  // across renders.
  const [isNavigating, setIsNavigating] = useState(false);
  useEffect(() => {
    if (!isNavigating) return;
    const timer = setTimeout(() => setIsNavigating(false), 500);
    return () => clearTimeout(timer);
  }, [isNavigating]);

  const { isClaimable, remainingMs } = useOnboardingDiscount();

  // Hide at the top of the Stripe-served ladder (Business, legacy or 2026):
  // there's nothing to upgrade to in-app (Custom is enterprise-only and not
  // bookable here). Free/Starter/etc. still see the CTA.
  if (
    !tier ||
    tier === TierEnum.CUSTOM ||
    tier === TierEnum.BUSINESS ||
    tier === TierEnum.BUSINESS_2026 ||
    !hasBillingAccess
  )
    return null;

  // Claim-discount variant: a fresh-out-of-onboarding TRIAL user with a
  // non-expired promo replaces the generic Upgrade CTA with a time-limited
  // claim button. Uses the same #B6D24E lime as the discount modal's gift
  // icon so the CTA is visually tied to the offer. Discount expiry, once
  // past, falls through to standard Upgrade for the rest of the trial.
  const isFreeTier = tier === TierEnum.TRIAL || tier === TierEnum.FREE;
  const showClaimDiscount = isClaimable && isFreeTier;
  // Free-tier users see the bold gradient + shine `upgrade` CTA (high-visibility
  // conversion ask). Paying users see the quieter `upgrade_subtle` variant —
  // they're already customers, so the upsell to a higher tier should feel
  // present but not pushy. Claim-discount stays custom (lime).
  const isTrial = isFreeTier;
  const variant = showClaimDiscount ? 'custom' : isTrial ? 'upgrade' : 'upgrade_subtle';
  const transparentWhenIdle = '';
  const claimDiscountClasses = showClaimDiscount
    ? '!border-transparent bg-lime-400 text-foreground hover:bg-lime-500'
    : '';
  const label = showClaimDiscount
    ? t('discount_modal.redeem_now', { ns: 'getting_started' })
    : t('button.upgrade_plan');
  const countdown = showClaimDiscount ? formatDiscountRemaining(remainingMs) : null;

  // Build an absolute path off the current `:scenario` URL param so the
  // button works from any nested route (KB, ArticleSheet at
  // /:scenario/knowledge-base/articles/:id, etc.). A bare `IndexPath.settings`
  // here is URL-relative and resolves to e.g. /:scenario/knowledge-base/settings,
  // which doesn't exist and falls back to the index.
  const handleClick = () => {
    if (!scenario || isNavigating) return;
    setIsNavigating(true);
    // Fire before navigate(); posthog-js flushes pending events on pagehide.
    capture(PostHogEventEnum.KbUpgradeCtaClicked, {
      source,
      tier,
      // Distinguish the lime "Claim discount" variant from the plain Upgrade CTA
      // so the onboarding-discount funnel can be segmented from generic upgrades.
      is_discount_claim: showClaimDiscount,
      ...(showClaimDiscount && { discount_remaining_ms: remainingMs }),
      articles_used: getConfig('articles_used') as number | undefined,
      articles_limit: getConfig('articles_limit') as number | null | undefined,
    });
    navigate(`/i/${scenario}/${IndexPath.settings}?tab=${SettingsTabsEnum.Billing}`, {
      viewTransition: true,
    });
  };

  const ButtonComponent = iconOnly ? IconButton : Button;
  const Icon = showClaimDiscount ? Gift : CircleArrowUp;
  const button = (
    <ButtonComponent
      size={size}
      variant={variant}
      onClick={handleClick}
      disabled={isNavigating}
      className={cn(transparentWhenIdle, claimDiscountClasses, className)}
      aria-label={iconOnly ? label : undefined}
    >
      {isNavigating ? <Spinner className="h-4 w-4 text-current" /> : <Icon />}
      {!iconOnly && <span className={showClaimDiscount ? 'hidden sm:inline' : ''}>{label}</span>}
      {!iconOnly && countdown && (
        <span className="ml-1 font-mono text-xs tabular-nums opacity-80">{countdown}</span>
      )}
    </ButtonComponent>
  );

  if (!iconOnly) return button;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent side="right">
          {label}
          {countdown && <span className="ml-1 font-mono tabular-nums">{countdown}</span>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
